import React, { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { login } from "../../store/actions/auth";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Fade,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import logoWhite from "../../../assets/img/logos/logoWhite.png";

const Login = () => {
  const dispatch = useDispatch();
  const { errorMsg } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: yup.object({
      email: yup
        .string("Ingresa tu correo")
        .email("Ingresa un correo válido")
        .required("El correo es obligatorio"),
      password: yup
        .string("Ingresa tu contraseña")
        .min(6, "Mínimo 6 caracteres")
        .required("La contraseña es obligatoria"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        dispatch(login(values.email, values.password, setLoading));
      } catch (error) {
        console.error("Error al iniciar sesión:", error);
        setLoading(false);
      }
    },
  });

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100vw",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: (theme) =>
          `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.background.paper} 100%)`,
        padding: 3,
        overflow: "auto",
      }}
    >
      <Fade in timeout={800}>
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 420,
            p: 5,
            borderRadius: 4,
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          {/* Logo Section */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                mx: "auto",
                mb: 2,
                borderRadius: 2,
                backgroundColor: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 14px rgba(201, 162, 39, 0.3)",
              }}
            >
              <img
                src={logoWhite}
                alt="NuCore"
                style={{ width: 54, height: 54, objectFit: "contain" }}
              />
            </Box>
            <Typography
              variant="h4"
              fontWeight={600}
              color="text.primary"
              sx={{ mb: 1 }}
            >
              Bienvenido
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Inicia sesión para gestionar el CRM de NuCore
            </Typography>
          </Box>

          {/* Error Alert */}
          {errorMsg && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                backgroundColor: "error.main",
                color: "white",
                "& .MuiAlert-icon": {
                  color: "white",
                },
              }}
            >
              {errorMsg}
            </Alert>
          )}

          {/* Form */}
          <Box component="form" onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              id="email"
              name="email"
              label="Correo electrónico"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              id="password"
              name="password"
              label="Contraseña"
              type={showPassword ? "text" : "password"}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              sx={{ mb: 4 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePassword}
                      edge="end"
                      size="small"
                      sx={{
                        color: "text.secondary",
                        "&:hover": {
                          color: "text.primary",
                        },
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: "0 4px 14px rgba(201, 162, 39, 0.3)",
                "&:hover": {
                  boxShadow: "0 6px 20px rgba(201, 162, 39, 0.4)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Iniciar sesión"
              )}
            </Button>
          </Box>

          {/* Footer */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 4,
              textAlign: "center",
              opacity: 0.7,
            }}
          >
            NuCore Admin CRM
          </Typography>
        </Paper>
      </Fade>
    </Box>
  );
};

export default Login;
